// src/pages/public/Home.js
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../../lib/firebase";
import PublicApplicationForm from "../PublicApplicationForm";

export default function Home() {
  const [user, setUser] = useState(null); // kept for future use; no longer drives the button
  const [loading, setLoading] = useState(true);

  // Login modal state
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  // Join (email) modal
  const [showJoin, setShowJoin] = useState(false);
  const [joinEmail, setJoinEmail] = useState("");
  const [joinBusy, setJoinBusy] = useState(false);
  const [joinErr, setJoinErr] = useState("");

  // Application modal
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyInitialEmail, setApplyInitialEmail] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const niceAuthError = (code, fallback) => {
    switch (code) {
      case "auth/invalid-credential":
      case "auth/wrong-password":
        return "Incorrect email or password.";
      case "auth/user-not-found":
        return "No account found for that email.";
      case "auth/too-many-requests":
        return "Too many attempts. Try again later or reset your password.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      default:
        return fallback || "Something went wrong. Please try again.";
    }
  };

  const openLogin = () => {
    setErr("");
    setMsg("");
    setPw("");
    setShowLogin(true);
  };

  const closeLogin = () => {
    setShowLogin(false);
    setBusy(false);
    setErr("");
    setMsg("");
  };

  const openJoin = () => {
    setJoinErr("");
    setJoinBusy(false);
    setJoinEmail("");
    setShowJoin(true);
  };

  const closeJoin = () => {
    setShowJoin(false);
    setJoinBusy(false);
    setJoinErr("");
  };

  const closeApplyModal = () => {
    setShowApplyModal(false);
    setApplyInitialEmail("");
  };

  // Disable background scroll (with scrollbar compensation to prevent page "jump")
  useEffect(() => {
    const anyModalOpen = showLogin || showJoin || showApplyModal;
    if (!anyModalOpen) return;

    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;

    document.body.style.overflow = "hidden";
    if (scrollBarWidth > 0) document.body.style.paddingRight = `${scrollBarWidth}px`;

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [showLogin, showJoin, showApplyModal]);

  // ESC handling
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (showApplyModal) closeApplyModal();
        else if (showJoin) closeJoin();
        else if (showLogin) closeLogin();
      }
    };

    if (showLogin || showJoin || showApplyModal) {
      window.addEventListener("keydown", onKey);
    }

    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLogin, showJoin, showApplyModal]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    if (busy) return;

    const formEmail = (email || "").trim().toLowerCase();
    const formPw = (pw || "").toString();

    if (!formEmail || !formPw) {
      setErr("Enter your email and password.");
      return;
    }

    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, formEmail, formPw);
      closeLogin();
      navigate("/dashboard", { replace: true });
    } catch (e2) {
      setErr(niceAuthError(e2.code, e2.message));
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    setErr("");
    setMsg("");
    const addr = (email || "").trim().toLowerCase();
    if (!addr) {
      setErr("Enter your email first.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, addr);
      setMsg("Reset link sent. Check your inbox (and Spam).");
    } catch (e2) {
      setErr(niceAuthError(e2.code, e2.message));
    }
  };

  // Join flow: email modal -> application modal (no route change)
  const handleJoinContinue = (e) => {
    e.preventDefault();
    if (joinBusy) return;

    setJoinErr("");
    const addr = (joinEmail || "").trim().toLowerCase();

    if (!addr) return setJoinErr("Please enter your email.");
    if (!/^\S+@\S+\.\S+$/.test(addr)) {
      return setJoinErr("Please enter a valid email address.");
    }

    setJoinBusy(true);
    try {
      closeJoin();
      setApplyInitialEmail(addr);
      setShowApplyModal(true);
    } finally {
      setJoinBusy(false);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;

  return (
    <div style={{ position: "relative" }}>
      <div style={topRightWrap}>
        <button type="button" onClick={openLogin} style={topRightBtn}>
          Member Login
        </button>
      </div>

      <section style={hero}>
        <div style={heroOverlay} />
        <div style={heroInner}>
          <div style={heroTopContent}>
            <h1 style={headline}>Travelers Championship Medical Team</h1>

            <p style={dateText}>June 22-28, 2026</p>

            <div style={heroActionWrap}>
              <button type="button" onClick={openJoin} style={applyBtnBtn}>
                Join our Team
              </button>

              <div style={secondaryLinkWrap}>
                Prefer the full page?{" "}
                <Link to="/apply" style={secondaryLink}>
                  Open application page
                </Link>
              </div>
            </div>
          </div>

          <div style={aboutPanel}>
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              The Travelers Championship Medical Team, proudly sponsored by Saint Francis Hospital,
              is composed of highly skilled medical professionals from across the care spectrum.
              Together, we deliver concierge-level medical care to spectators, vendors, volunteers,
              PGA TOUR players, and their families.
              <br />
              <br />
              Consistently recognized as “the best on tour,” our team is driven by a simple
              philosophy: we work relentlessly to get to “yes.” No request is too small and no
              emergency too large. From a simple bandage to a critical, time-sensitive event, every
              individual we care for is treated like a VIP.
              <br />
              <br />
              If you’re interested in being part of an elite, patient-focused medical team, click{" "}
              <strong>Join our Team</strong> above to begin your application.
            </p>
          </div>
        </div>
      </section>

      {showJoin && (
        <div style={modalOverlay} onMouseDown={closeJoin} role="presentation">
          <div
            style={modalCard}
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div style={modalHeader}>
              <div style={{ fontWeight: 800, fontSize: 18 }}>Join our Team</div>
              <button type="button" onClick={closeJoin} style={iconCloseBtn} aria-label="Close">
                ✕
              </button>
            </div>

            <form onSubmit={handleJoinContinue} style={{ marginTop: 10 }}>
              <label style={label} htmlFor="join-email">
                Email
              </label>
              <input
                id="join-email"
                type="email"
                autoComplete="email"
                value={joinEmail}
                onChange={(e) => setJoinEmail(e.target.value)}
                disabled={joinBusy}
                style={input}
                placeholder="you@example.com"
              />

              {joinErr && (
                <div style={errorText} role="alert">
                  {joinErr}
                </div>
              )}

              <button type="submit" disabled={joinBusy} style={primaryBtn}>
                {joinBusy ? "Continuing…" : "Continue"}
              </button>
            </form>
          </div>
        </div>
      )}

      {showApplyModal && (
        <div style={modalOverlay} onMouseDown={closeApplyModal} role="presentation">
          <div
            style={applyModalCard}
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Application"
          >
            <div style={applyModalHeader}>
              <div style={{ fontWeight: 900, fontSize: 18 }}>Application</div>

              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button
                  type="button"
                  onClick={closeApplyModal}
                  style={iconCloseBtnDark}
                  aria-label="Close application"
                  title="Close"
                >
                  ✕
                </button>
              </div>
            </div>

            <div style={applyModalBody} id="apply-modal-body">
              <PublicApplicationForm
                key={applyInitialEmail || "new"}
                initialEmail={applyInitialEmail}
                onDone={closeApplyModal}
              />
            </div>
          </div>
        </div>
      )}

      {showLogin && (
        <div style={modalOverlay} onMouseDown={closeLogin} role="presentation">
          <div
            style={modalCard}
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div style={modalHeader}>
              <div style={{ fontWeight: 800, fontSize: 18 }}>Member Login</div>
              <button type="button" onClick={closeLogin} style={iconCloseBtn} aria-label="Close login">
                ✕
              </button>
            </div>

            <form onSubmit={handleSignIn} style={{ marginTop: 10 }}>
              <label style={label} htmlFor="modal-email">
                Email
              </label>
              <input
                id="modal-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                style={input}
                placeholder="you@example.com"
              />

              <label style={label} htmlFor="modal-password">
                Password
              </label>
              <input
                id="modal-password"
                type="password"
                autoComplete="current-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                disabled={busy}
                style={input}
                placeholder="••••••••"
              />

              {err && <div style={errorText}>{err}</div>}
              {msg && <div style={msgText}>{msg}</div>}

              <button type="submit" disabled={busy} style={primaryBtn}>
                {busy ? "Signing in…" : "Sign in"}
              </button>

              <button type="button" onClick={handleReset} disabled={busy} style={linkBtn}>
                Forgot password?
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Styles ---------------- */

const hero = {
  minHeight: "100svh",
  position: "relative",
  backgroundImage: "url('/images/home-hero-v2.jpg')",
  backgroundSize: "cover",
  backgroundPosition: "center 40%",
  color: "#fff",
  overflow: "hidden",
};

const heroOverlay = {
  position: "absolute",
  inset: 0,
  background: "linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.45))",
};

const heroInner = {
  position: "relative",
  minHeight: "100svh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-between",
  textAlign: "center",
  gap: 24,
  padding: "clamp(88px, 12vw, 120px) clamp(14px, 3vw, 28px) clamp(18px, 4vw, 32px)",
};

const heroTopContent = {
  width: "100%",
  maxWidth: 1100,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const headline = {
  fontSize: "clamp(2rem, 6vw, 3.5rem)",
  fontWeight: 900,
  marginTop: 0,
  marginBottom: 12,
  letterSpacing: 0.3,
  lineHeight: 1.05,
  color: "#ffffff",
  textShadow: "0 3px 14px rgba(0,0,0,0.65)",
  filter: `
    drop-shadow(0 0 10px rgba(97, 54, 255, 0.35))
    drop-shadow(0 0 8px rgba(47, 161, 92, 0.28))
  `,
};

const dateText = {
  fontSize: "clamp(1rem, 2.6vw, 1.125rem)",
  maxWidth: 900,
  margin: "0 auto",
  lineHeight: 1.4,
};

const heroActionWrap = {
  marginTop: "clamp(18px, 3vw, 26px)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
};

const secondaryLinkWrap = {
  marginTop: 10,
  fontSize: 12,
  opacity: 0.9,
  paddingLeft: 8,
  paddingRight: 8,
};

const secondaryLink = {
  color: "#fff",
  textDecoration: "underline",
};

const aboutPanel = {
  width: "min(1100px, 100%)",
  maxHeight: "min(34vh, 260px)",
  padding: "clamp(14px, 2.6vw, 22px) clamp(16px, 3vw, 24px)",
  borderRadius: 16,
  background: "rgba(0,0,0,0.55)",
  backdropFilter: "blur(6px)",
  border: "1px solid rgba(255,255,255,0.25)",
  color: "#fff",
  textAlign: "left",
  overflowY: "auto",
  boxSizing: "border-box",
};

const topRightWrap = {
  position: "absolute",
  top: "max(16px, env(safe-area-inset-top))",
  right: "max(16px, env(safe-area-inset-right))",
  zIndex: 10,
};

const topRightBtn = {
  color: "#fff",
  fontWeight: 800,
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.85)",
  background: "rgba(0,0,0,0.35)",
  cursor: "pointer",
  fontSize: 14,
  backdropFilter: "blur(4px)",
};

const applyBtnBtn = {
  display: "inline-block",
  width: "min(100%, 260px)",
  background: "#ffffff",
  color: "#111827",
  padding: "14px 22px",
  borderRadius: 12,
  fontWeight: 900,
  fontSize: 16,
  border: "none",
  cursor: "pointer",
  boxShadow: "0 12px 28px rgba(0,0,0,0.22)",
};

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  zIndex: 9999,
};

const modalCard = {
  width: "100%",
  maxWidth: 420,
  background: "#ffffff",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
};

const applyModalCard = {
  width: "min(980px, 96vw)",
  height: "min(85vh, 92vh)",
  background: "#ffffff",
  borderRadius: 16,
  boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const applyModalHeader = {
  position: "sticky",
  top: 0,
  zIndex: 5,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 16px",
  borderBottom: "1px solid #e5e7eb",
};

const applyModalBody = {
  padding: "clamp(10px, 2.2vw, 16px)",
  overflowY: "auto",
  flex: 1,
};

const modalHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const iconCloseBtn = {
  border: "none",
  background: "#f3f4f6",
  color: "#111827",
  fontSize: 16,
  cursor: "pointer",
  width: 34,
  height: 34,
  borderRadius: 10,
};

const iconCloseBtnDark = {
  border: "none",
  background: "#111827",
  color: "#fff",
  fontSize: 16,
  cursor: "pointer",
  width: 34,
  height: 34,
  borderRadius: 10,
};

const label = {
  display: "block",
  fontWeight: 700,
  fontSize: 13,
  color: "#111827",
  marginTop: 12,
  marginBottom: 6,
};

const input = {
  width: "100%",
  padding: "11px 12px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  outline: "none",
  fontSize: 14,
  boxSizing: "border-box",
};

const primaryBtn = {
  width: "100%",
  marginTop: 14,
  padding: "12px 14px",
  borderRadius: 12,
  border: "1px solid #111827",
  background: "#111827",
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
};

const linkBtn = {
  width: "100%",
  marginTop: 10,
  padding: "10px 14px",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  background: "#fff",
  color: "#111827",
  fontWeight: 800,
  cursor: "pointer",
};

const errorText = { marginTop: 10, color: "#b91c1c", fontWeight: 700, fontSize: 13 };
const msgText = { marginTop: 10, color: "#065f46", fontWeight: 700, fontSize: 13 };